import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { App, Button, Drawer, Empty, Input, List, Modal, Popconfirm, Space, Tag, Typography } from 'antd';
import { DeleteOutlined, FolderOpenOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  createOrderDraft,
  deleteOrderDraft,
  fetchOrderDraft,
  listOrderDrafts,
  updateOrderDraft,
  OrderDraftSummary,
  OrderDraftType,
} from '../../modules/orders/api/orderDraftsApi';

const { Text } = Typography;

export interface OrderDraftToolbarProps {
  /** Which wizard this toolbar serves. */
  type: OrderDraftType;
  /** Build the JSON-serializable payload from current form state (dates → ISO). */
  buildPayload: () => Record<string, any>;
  /** Apply a loaded draft payload back into the form (ISO → dayjs, role stripping). */
  applyPayload: (payload: Record<string, any>) => void;
  /** Suggested name when saving a brand-new draft (e.g. customer/supplier name). */
  defaultDraftName?: () => string;
  /** Notified after any successful save (e.g. to refresh a "last saved" indicator). */
  onSaved?: () => void;
}

export interface OrderDraftToolbarHandle {
  /**
   * Delete the currently-loaded draft after an order was created from it.
   * Best-effort: a failed delete never blocks the create success flow.
   */
  discardLoadedDraft: () => Promise<void>;
}

/**
 * Shared draft controls for both order-create wizards (single home for the
 * save/load/list/delete UI). Drafts are persisted server-side, so they survive
 * tab close, logout, and device changes, and never leak between users (the
 * backend scopes every request to the authenticated user).
 */
const OrderDraftToolbar = forwardRef<OrderDraftToolbarHandle, OrderDraftToolbarProps>(
  ({ type, buildPayload, applyPayload, defaultDraftName, onSaved }, ref) => {
    const { t } = useTranslation(['orders', 'common']);
    const { message } = App.useApp();

    const [drafts, setDrafts] = useState<OrderDraftSummary[]>([]);
    const [listLoading, setListLoading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [saveModalOpen, setSaveModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
    const [currentDraftName, setCurrentDraftName] = useState<string | null>(null);

    const refreshList = async () => {
      setListLoading(true);
      try {
        setDrafts(await listOrderDrafts(type));
      } catch {
        message.error(t('orders:wizard.drafts.listError'));
      } finally {
        setListLoading(false);
      }
    };

    // Load the draft count once on mount so the "Your drafts (N)" button is honest.
    useEffect(() => {
      void refreshList();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type]);

    const suggestedName = () => {
      const fromWizard = defaultDraftName?.()?.trim();
      if (fromWizard) return fromWizard;
      return t('orders:wizard.drafts.defaultName', { date: dayjs().format('YYYY-MM-DD HH:mm') });
    };

    const openSaveModal = (prefill: string) => {
      setNameInput(prefill);
      setSaveModalOpen(true);
    };

    // "Save draft": update the loaded draft in place, or open the name modal to
    // create the first one.
    const handleSaveClick = async () => {
      if (!currentDraftId) {
        openSaveModal(suggestedName());
        return;
      }
      setSaving(true);
      try {
        const updated = await updateOrderDraft(currentDraftId, {
          name: currentDraftName || suggestedName(),
          payload: buildPayload(),
        });
        setCurrentDraftName(updated.name);
        message.success(t('orders:wizard.drafts.saved'));
        onSaved?.();
        if (drawerOpen) void refreshList();
      } catch {
        message.error(t('orders:wizard.drafts.saveError'));
      } finally {
        setSaving(false);
      }
    };

    const handleSaveAsNew = () => {
      const base = currentDraftName
        ? `${currentDraftName} ${t('orders:wizard.drafts.copySuffix')}`
        : suggestedName();
      openSaveModal(base);
    };

    const confirmSaveNew = async () => {
      const name = nameInput.trim();
      if (!name) {
        message.error(t('orders:wizard.drafts.nameRequired'));
        return;
      }
      setSaving(true);
      try {
        const created = await createOrderDraft({ type, name, payload: buildPayload() });
        setCurrentDraftId(created.id);
        setCurrentDraftName(created.name);
        message.success(t('orders:wizard.drafts.saved'));
        onSaved?.();
        setSaveModalOpen(false);
        if (drawerOpen) void refreshList();
      } catch (error: any) {
        const code = error?.response?.data?.code;
        message.error(
          code === 'DRAFT_LIMIT_REACHED'
            ? t('orders:wizard.drafts.limitReached')
            : t('orders:wizard.drafts.saveError')
        );
      } finally {
        setSaving(false);
      }
    };

    const handleLoad = async (id: string) => {
      try {
        const draft = await fetchOrderDraft(id);
        applyPayload(draft.payload);
        setCurrentDraftId(draft.id);
        setCurrentDraftName(draft.name);
        setDrawerOpen(false);
        message.info(t('orders:wizard.drafts.loaded'));
      } catch {
        message.error(t('orders:wizard.drafts.loadError'));
      }
    };

    const handleDelete = async (id: string) => {
      try {
        await deleteOrderDraft(id);
        if (id === currentDraftId) {
          setCurrentDraftId(null);
          setCurrentDraftName(null);
        }
        message.success(t('orders:wizard.drafts.deleted'));
        void refreshList();
      } catch {
        message.error(t('orders:wizard.drafts.deleteError'));
      }
    };

    const openDrawer = () => {
      setDrawerOpen(true);
      void refreshList();
    };

    useImperativeHandle(
      ref,
      () => ({
        discardLoadedDraft: async () => {
          if (!currentDraftId) return;
          try {
            await deleteOrderDraft(currentDraftId);
          } catch {
            // Best-effort: never block the order-create success flow.
          }
          setCurrentDraftId(null);
          setCurrentDraftName(null);
        },
      }),
      [currentDraftId]
    );

    return (
      <>
        <Space wrap>
          {currentDraftName ? (
            <Tag color="blue">{t('orders:wizard.drafts.editing', { name: currentDraftName })}</Tag>
          ) : null}
          <Button icon={<SaveOutlined />} onClick={handleSaveClick} loading={saving}>
            {t('orders:wizard.drafts.save')}
          </Button>
          {currentDraftId ? (
            <Button onClick={handleSaveAsNew}>{t('orders:wizard.drafts.saveAsNew')}</Button>
          ) : null}
          <Button icon={<FolderOpenOutlined />} onClick={openDrawer}>
            {t('orders:wizard.drafts.yourDrafts', { value: drafts.length })}
          </Button>
        </Space>

        <Drawer
          title={t('orders:wizard.drafts.drawerTitle')}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={420}
        >
          <List
            loading={listLoading}
            dataSource={drafts}
            locale={{ emptyText: <Empty description={t('orders:wizard.drafts.empty')} /> }}
            renderItem={(draft) => (
              <List.Item
                actions={[
                  <Button key="load" type="link" onClick={() => handleLoad(draft.id)}>
                    {t('orders:wizard.drafts.load')}
                  </Button>,
                  <Popconfirm
                    key="delete"
                    title={t('orders:wizard.drafts.deleteConfirm')}
                    okText={t('common:yes')}
                    cancelText={t('common:no')}
                    onConfirm={() => handleDelete(draft.id)}
                  >
                    <Button type="link" danger icon={<DeleteOutlined />} />
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  title={draft.name}
                  description={
                    <Text type="secondary">
                      {t('orders:wizard.drafts.updatedAt', {
                        date: dayjs(draft.updatedAt).format('YYYY-MM-DD HH:mm'),
                      })}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        </Drawer>

        <Modal
          title={t('orders:wizard.drafts.saveModalTitle')}
          open={saveModalOpen}
          onOk={confirmSaveNew}
          onCancel={() => setSaveModalOpen(false)}
          okText={t('common:save')}
          cancelText={t('common:cancel')}
          confirmLoading={saving}
        >
          <Text type="secondary">{t('orders:wizard.drafts.nameLabel')}</Text>
          <Input
            value={nameInput}
            onChange={(event) => setNameInput(event.target.value)}
            placeholder={t('orders:wizard.drafts.namePlaceholder')}
            maxLength={120}
            onPressEnter={confirmSaveNew}
            autoFocus
            style={{ marginTop: 8 }}
          />
        </Modal>
      </>
    );
  }
);

OrderDraftToolbar.displayName = 'OrderDraftToolbar';

export default OrderDraftToolbar;
